import GrmMediapipeLegacy from "./grm-mediapipe-legacy";

export default class GrmMediapipeWebGl extends GrmMediapipeLegacy {
    #vertexPositionLocation;
    #buffer;
    #textureLocation;
    #equations;
    #functions;
    // #fpsControl;
    #matrixLocation;
    #vao;
    
    constructor(options={}) {
        super(options);
    }
    getCanvas(){
        if(this.canvas){
            return this.canvas;
        }

        const canvas = this.canvas = document.createElement('canvas');
        this.canvasCtx = canvas.getContext('webgl2', { premultipliedAlpha: false });
        
        return this.canvas;
    }
    async setBlurBackground(){
        const canvasCtx = this.getCanvasCtx();
        const canvas = canvasCtx.canvas;
        canvas.dispatchEvent(new Event('cleanUp'));

        // create shaders
        let simpleVertexShaderSrc = `
                    attribute vec2 a_position;
                    attribute vec2 a_texCoord;
                    
                    uniform vec2 u_resolution;
                    uniform float u_blur;
                    
                    varying vec2 v_texCoord;
                    varying vec2 v_resolution;
                    varying float v_blur;
                    
                    void main() {           
                            // convert the rectangle from pixels to 0.0 to 1.0
                            vec2 zeroToOne = a_position / u_resolution;
                            
                            // convert from 0->1 to 0->2
                            vec2 zeroToTwo = zeroToOne * 2.0;
                            
                            // convert from 0->2 to -1->+1 (clipspace)
                            vec2 clipSpace = zeroToTwo - 1.0;
                            
                            gl_Position = vec4(clipSpace * vec2(1, -1), 0, 1);
                            
                            // pass the texCoord to the fragment shader
                            // The GPU will interpolate this value between points.
                            v_texCoord = a_texCoord;
                        
                        
                        v_resolution = u_resolution;
                        v_blur = u_blur;
                    }`;

        const kernelRadius = 9;
        const kernelSize = kernelRadius * kernelRadius;
        const kernelWeight = '1.0';
        // kernelWeight = this.#computeKernelWeight(kernel); // 추후 커널 변경시 사용 필요

        const colorSumStr = (() => {
            let arr = [];
            let kernelCoord = Math.floor(kernelRadius / 2);
            let meanFilter = 1/kernelSize;

            for(let n1 = -kernelCoord; n1 < kernelRadius / 2; n1++){
                for(let n2 = -kernelCoord; n2 < kernelRadius / 2; n2++){
                    arr.push(`texture2D(u_image, v_texCoord + onePixel * vec2(${n1}, ${n2})) * ${meanFilter}`);
                }
            }
            return arr.join('+');
        })();

        let simpleFragmentShaderSrc = `
                    // precision highp 를 지원하지 않는 일부 기기를 위한 호환성 체크
                    #ifdef GL_FRAGMENT_PRECISION_HIGH
                      precision highp float;
                    #else
                      precision mediump float;
                    #endif                    
                    
                    uniform sampler2D u_image;
                    uniform vec2 u_textureSize;
                                        
                    varying vec2 v_texCoord;
                    varying float v_blur;
                    varying vec2 v_resolution;
                    
                    void main() {
                        if(v_blur < 0.5){
                            vec2 coord = gl_FragCoord.xy / v_resolution.xy;
                            coord.y = 1.0 - coord.y;
                            gl_FragColor = texture2D( u_image, coord ).rgba;
                            
                        } else if(v_blur > 0.5){                      
                            vec2 onePixel = vec2(1.0, 1.0) / u_textureSize;
                            vec4 colorSum = ${colorSumStr};
                            gl_FragColor = vec4((colorSum / ${kernelWeight}).rgb, 1);
                        }
                    }
                    `;

        let vertShader = this.#createShader(canvasCtx, canvasCtx.VERTEX_SHADER, simpleVertexShaderSrc);
        let fragShader = this.#createShader(canvasCtx, canvasCtx.FRAGMENT_SHADER, simpleFragmentShaderSrc);
        let program = this.#createProgram(canvasCtx, vertShader, fragShader);

        this.resolutionLocation = canvasCtx.getUniformLocation(program, "u_resolution");

        canvasCtx.useProgram(program);

        // look up where the vertex data needs to go.
        this.positionLocation = canvasCtx.getAttribLocation(program, "a_position");
        this.texcoordLocation = canvasCtx.getAttribLocation(program, "a_texCoord");
        this.textureLocation = canvasCtx.getUniformLocation(program, "u_image");
        this.blurLocation = canvasCtx.getUniformLocation(program, "u_blur");

        let positionBuffer = canvasCtx.createBuffer();
        let texcoordBuffer = canvasCtx.createBuffer();
        canvasCtx.bindBuffer(canvasCtx.ARRAY_BUFFER, texcoordBuffer);
        canvasCtx.bufferData(canvasCtx.ARRAY_BUFFER, new Float32Array([
            0.0,  0.0,
            1.0,  0.0,
            0.0,  1.0,
            0.0,  1.0,
            1.0,  0.0,
            1.0,  1.0,
        ]), canvasCtx.STATIC_DRAW);

        // lookup uniforms
        this.textureSizeLocation = canvasCtx.getUniformLocation(program, "u_textureSize");

        let resizeEventHandler = e => {
            canvasCtx.viewport(0, 0, canvas.width, canvas.height);
            canvasCtx.uniform2f( this.resolutionLocation, canvas.width, canvas.height);

            canvasCtx.bindBuffer(canvasCtx.ARRAY_BUFFER, positionBuffer);
            this.#setRectangle( canvasCtx, 0, 0, canvas);
        };
        canvas.addEventListener('resize', resizeEventHandler);
        this.syncStreamAndCanvas();
        canvas.addEventListener('cleanUp', e => {
            canvasCtx.deleteShader(vertShader);
            canvasCtx.deleteShader(fragShader);
            canvasCtx.deleteProgram(program);
            canvas.removeEventListener('resize', resizeEventHandler);
        }, {once:true});

        canvasCtx.enable(canvasCtx.BLEND);

        return this.setBackground({onResult: (results) => {
                this.fpsControl && this.fpsControl.tick();
                let oriTexture = this.#loadTexture(canvasCtx, results.image);

                canvasCtx.clearColor(0, 0, 0, 0);
                canvasCtx.clear(canvasCtx.COLOR_BUFFER_BIT);

                canvasCtx.enableVertexAttribArray(this.positionLocation);
                canvasCtx.bindBuffer(canvasCtx.ARRAY_BUFFER, positionBuffer);
                // Tell the position attribute how to get data out of positionBuffer (ARRAY_BUFFER)
                let size = 2;          // 2 components per iteration
                let type = canvasCtx.FLOAT;   // the data is 32bit floats
                let normalize = false; // don't normalize the data
                let stride = 0;        // 0 = move forward size * sizeof(type) each iteration to get the next position
                let offset = 0;        // start at the beginning of the buffer
                canvasCtx.vertexAttribPointer(
                    this.positionLocation, size, type, normalize, stride, offset);


                canvasCtx.enableVertexAttribArray(this.texcoordLocation);
                canvasCtx.bindBuffer(canvasCtx.ARRAY_BUFFER, texcoordBuffer);
                canvasCtx.vertexAttribPointer(
                    this.texcoordLocation, size, type, normalize, stride, offset);

                canvasCtx.uniform2f(this.textureSizeLocation, results.image.width, results.image.height);

                canvasCtx.uniform1i( this.textureLocation, 0 );
                canvasCtx.activeTexture( canvasCtx.TEXTURE0 );
                canvasCtx.bindTexture( canvasCtx.TEXTURE_2D, oriTexture);

                canvasCtx.uniform1f(this.blurLocation, 1);
                canvasCtx.drawArrays(canvasCtx.TRIANGLES, 0, 6 );
                canvasCtx.uniform1f(this.blurLocation, 0);

                canvasCtx.enable(canvasCtx.BLEND);

                canvasCtx.blendEquation(canvasCtx.FUNC_ADD);
                canvasCtx.blendFunc(
                    canvasCtx.ZERO,
                    canvasCtx.ONE_MINUS_SRC_ALPHA
                );
                canvasCtx.bindTexture( canvasCtx.TEXTURE_2D, this.#loadTexture(canvasCtx,results.segmentationMask));
                canvasCtx.drawArrays( canvasCtx.TRIANGLES, 0, 6 );


                canvasCtx.blendEquation(canvasCtx.FUNC_ADD);
                canvasCtx.blendFunc(
                    canvasCtx.ONE_MINUS_DST_ALPHA,
                    canvasCtx.ONE
                );
                canvasCtx.bindTexture( canvasCtx.TEXTURE_2D, this.#loadTexture(canvasCtx, results.image));
                canvasCtx.drawArrays( canvasCtx.TRIANGLES, 0, 6 );
            }})
    }
    async setBackgroundImage(options={}){
        let img = options.img;
        if(!img){
            throw 'img is null';
        }
        // if(typeof img)
        if(img instanceof HTMLImageElement){
            if(!img.complete){
                return new Promise((resolve, reject) => {
                    img.addEventListener('load', e => {
                        resolve(
                            this.setBackgroundImage.apply(this, arguments)
                        );
                    }, {once: true});
                    img.addEventListener('error', e => {
                        reject(e);
                    }, {once: true});
                })
            }
        }else if(typeof img === 'string'){
            options.img = new Image();
            options.img.src = img;
            return this.setBackgroundImage(options);
        }

        const canvasCtx = this.getCanvasCtx();
        const canvas = canvasCtx.canvas;
        canvas.dispatchEvent(new Event('cleanUp'));

        // create shaders
        let simpleVertexShaderSrc = `
                    attribute vec3 position;
    
                    void main() {        
                        gl_Position = vec4( position, 1.0 );        
                    }`;

        let simpleFragmentShaderSrc = `
                    // precision highp 를 지원하지 않는 일부 기기를 위한 호환성 체크
                    #ifdef GL_FRAGMENT_PRECISION_HIGH
                      precision highp float;
                    #else
                      precision mediump float;
                    #endif
                    
                    uniform vec2 resolution;
                    uniform sampler2D texture;
                    
                    void main( void ) {
                        vec2 coord = gl_FragCoord.xy / resolution.xy;
                        coord.y = 1.0 - coord.y;
                        gl_FragColor = texture2D( texture, coord ).rgba;
                    }`;

        let vertShader = this.#createShader(canvasCtx, canvasCtx.VERTEX_SHADER, simpleVertexShaderSrc);
        let fragShader = this.#createShader(canvasCtx, canvasCtx.FRAGMENT_SHADER, simpleFragmentShaderSrc);
        let program = this.#createProgram(canvasCtx, vertShader, fragShader);

        let resizeEventHandler = e => {
            canvasCtx.viewport(0, 0, canvas.width, canvas.height);
            canvasCtx.uniform2f( canvasCtx.getUniformLocation( program, 'resolution' ), canvas.width, canvas.height);
        };
        canvas.addEventListener('resize', resizeEventHandler);

        canvasCtx.useProgram(program);

        let buffer = this.buffer = canvasCtx.createBuffer();
        canvasCtx.bindBuffer( canvasCtx.ARRAY_BUFFER, buffer );
        canvasCtx.bufferData( canvasCtx.ARRAY_BUFFER, new Float32Array( [
            - 1.0, - 1.0, 1.0,
            - 1.0, - 1.0, 1.0,
            1.0, - 1.0, 1.0,
            1.0, - 1.0, 1.0
        ] ), canvasCtx.STATIC_DRAW );

        // Get var locations
        this.vertexPositionLocation = canvasCtx.getAttribLocation( program, 'position' );
        this.textureLocation = canvasCtx.getUniformLocation( program, 'texture' );

        this.syncStreamAndCanvas();
        canvas.addEventListener('cleanUp', e => {
            canvasCtx.deleteShader(vertShader);
            canvasCtx.deleteShader(fragShader);
            canvasCtx.deleteProgram(program);
            canvasCtx.deleteBuffer(buffer);
            canvas.removeEventListener('resize', resizeEventHandler);
        }, {once:true});

        const bgTexture = this.#loadTexture(canvasCtx, img);
        canvasCtx.enable(canvasCtx.BLEND);

        return this.setBackground({onResult: (results) => {
                this.fpsControl && this.fpsControl.tick();

                canvasCtx.clearColor(0, 0, 0, 0);
                canvasCtx.clear( canvasCtx.COLOR_BUFFER_BIT | canvasCtx.DEPTH_BUFFER_BIT );

                canvasCtx.uniform1i( this.textureLocation, 0 );
                canvasCtx.activeTexture( canvasCtx.TEXTURE0 );
                canvasCtx.bindTexture( canvasCtx.TEXTURE_2D, bgTexture);

                // Render geometry
                canvasCtx.bindBuffer( canvasCtx.ARRAY_BUFFER, this.buffer );
                canvasCtx.vertexAttribPointer( this.vertexPositionLocation, 2, canvasCtx.FLOAT, false, 0, 0 );
                canvasCtx.enableVertexAttribArray( this.vertexPositionLocation );
                canvasCtx.drawArrays( canvasCtx.TRIANGLES, 0, 6 );


                canvasCtx.blendEquation(canvasCtx.FUNC_ADD);
                canvasCtx.blendFunc(
                    canvasCtx.ZERO,
                    canvasCtx.ONE_MINUS_SRC_ALPHA
                );
                canvasCtx.bindTexture( canvasCtx.TEXTURE_2D, this.#loadTexture(canvasCtx,results.segmentationMask));
                canvasCtx.drawArrays( canvasCtx.TRIANGLES, 0, 6 );


                canvasCtx.blendEquation(canvasCtx.FUNC_ADD);
                canvasCtx.blendFunc(
                    canvasCtx.ONE_MINUS_DST_ALPHA,
                    canvasCtx.ONE
                );
                canvasCtx.bindTexture( canvasCtx.TEXTURE_2D, this.#loadTexture(canvasCtx, results.image));
                canvasCtx.drawArrays( canvasCtx.TRIANGLES, 0, 6 );
            }})
    }

    #createShader(gl, type, source) {
        let shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        let success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
        if (success) {
            return shader;
        }

        console.log(gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
    }
    #createProgram(gl, vertexShader, fragmentShader) {
        let program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);
        let success = gl.getProgramParameter(program, gl.LINK_STATUS);
        if (success) {
            return program;
        }

        console.log(gl.getProgramInfoLog(program));
        gl.deleteProgram(program);
    }
    #loadTexture(gl, image) {
        let texture = gl.createTexture();

        // gl.enable( gl.TEXTURE_2D );
        gl.bindTexture( gl.TEXTURE_2D, texture );
        gl.texImage2D( gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image );
        gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR );
        gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR );
        gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE );
        gl.texParameteri( gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE );
        gl.generateMipmap( gl.TEXTURE_2D );
        gl.bindTexture( gl.TEXTURE_2D, null );

        return texture;
    }
    #computeKernelWeight(kernel) {
        var weight = kernel.reduce(function(prev, curr) {
            return prev + curr;
        });
        return weight <= 0 ? 1 : weight;
    }
    #setRectangle(gl, x, y, {width, height}) {
        var x1 = x;
        var x2 = x + width;
        var y1 = y;
        var y2 = y + height;
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            x1, y1,
            x2, y1,
            x1, y2,
            x1, y2,
            x2, y1,
            x2, y2,
        ]), gl.STATIC_DRAW);
    }
}